import React from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  ScanSearch,
  Layers,
  History,
  LogOut,
  ShieldCheck,
  CreditCard,
  BarChart3,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AccountProvider, useAccount } from "@/context/AccountContext";
import { LOGO_URL } from "@/lib/mockData";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/analyze", label: "New analysis", icon: ScanSearch, testid: "nav-analyze" },
  { to: "/batches", label: "Batches", icon: Layers, testid: "nav-batches" },
  { to: "/history", label: "Case history", icon: History, testid: "nav-history" },
  { to: "/account", label: "Account & plan", icon: CreditCard, testid: "nav-account" },
];

const TIER_PILL = {
  free: "border-zinc-600/50 text-zinc-300",
  plus: "border-sky-500/40 text-sky-300",
  pro: "border-emerald-500/40 text-emerald-300",
};

function TierPill() {
  const { account } = useAccount();
  if (!account) return null;
  const tier = account.effective_tier || "free";
  const u = account.usage || {};
  const usage = u.admin_exempt ? "admin" : u.unlimited ? "unlimited" : `${u.used_today ?? 0}/${u.daily_limit} today`;
  return (
    <NavLink
      to="/account"
      data-testid="tier-pill"
      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-[11px] uppercase tracking-wider ${TIER_PILL[tier]}`}
    >
      <span className="font-semibold">{account.tier_label}</span>
      <span className="text-zinc-500 normal-case tracking-normal">{usage}</span>
    </NavLink>
  );
}

export default function Layout() {
  return (
    <AccountProvider>
      <LayoutInner />
    </AccountProvider>
  );
}

function LayoutInner() {
  const { user, signOut } = useAuth();
  const { isAdmin } = useAccount();
  const navigate = useNavigate();
  const navItems = isAdmin
    ? [...NAV, { to: "/admin", label: "Admin", icon: BarChart3, testid: "nav-admin" }]
    : NAV;

  const handleSignOut = () => {
    signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-zinc-100 grid grid-cols-[260px_1fr]">
      <aside
        className="border-r border-[#272A30] bg-[#0A0A0B] flex flex-col"
        data-testid="sidebar"
      >
        <div className="px-6 py-5 border-b border-[#272A30] flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-white/5 ring-1 ring-white/10 flex items-center justify-center overflow-hidden">
            <img
              src={LOGO_URL}
              alt="Yoobikwitus"
              className="h-7 w-7 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight" data-testid="brand-name">
              Yoobikwitus
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              Verification suite
            </div>
          </div>
        </div>

        <nav className="px-3 py-4 flex-1 space-y-1" aria-label="Primary">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-[#1A1D24] text-white"
                    : "text-zinc-400 hover:text-white hover:bg-[#14171D]"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-[#272A30] p-4 space-y-3">
          <div className="flex items-center gap-3 px-1">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-sky-500/40 to-emerald-500/40 ring-1 ring-white/10 flex items-center justify-center text-xs font-semibold">
              {(user?.user_metadata?.name || user?.email)?.[0]?.toUpperCase() || "Y"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">
                {user?.user_metadata?.name || "Picture Editor"}
              </div>
              <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            data-testid="sign-out-button"
            className="w-full flex items-center justify-center gap-2 rounded-lg border border-[#272A30] bg-transparent px-3 py-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-white hover:bg-[#14171D] transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </aside>

      <main className="min-h-screen flex flex-col" data-testid="app-main">
        <div className="border-b border-[#272A30] px-8 py-3 flex items-center justify-between bg-[#0A0A0B]/80 backdrop-blur">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
            <span>Secure session</span>
            <span className="text-zinc-700">•</span>
            <span className="mono">api.yoobikwitus.com</span>
          </div>
          <div className="flex items-center gap-4">
            <TierPill />
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-600">
              Newsroom build · v1.0
            </div>
          </div>
        </div>
        <div className="flex-1 px-8 py-8">
          <Outlet />
        </div>
      </main>

      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          classNames: {
            toast:
              "!bg-[#111318] !border !border-[#272A30] !text-zinc-100 !rounded-xl !shadow-[0_8px_32px_-8px_rgba(0,0,0,0.6)]",
            description: "!text-zinc-400",
          },
        }}
      />
    </div>
  );
}
